import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  type Row,
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';

import Grid from 'shared/components/Grid';

import useSelector from 'shared/hooks/useSelector';
import useEntityDialogState from 'shared/hooks/useEntityDialogState';

import { reset } from '../../slice';
import type { UserCommonCase } from '../../types';

import Dialog from 'shared/components/Dialog';

import { selectFilteredBySearchTermUserCommonCasesList } from '../../selectors';
import UserCommonCaseViewForm from '../UserCommonCaseViewForm';
import useColumns from './useColumns';

type CommonCasesGridProps = {
  isFetching: boolean;
};

const CommonCasesGrid = ({ isFetching }: CommonCasesGridProps) => {
  const entityDialogState = useEntityDialogState();

  const commonCases = useSelector(
    selectFilteredBySearchTermUserCommonCasesList
  );

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: 'CaseLevel1_Label', value: [] },
    { id: 'Status_Label', value: [] },
    { id: 'CreatedOn', value: [] },
  ]);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'CreatedOn', desc: true },
  ]);

  const columns = useColumns();

  const table = useReactTable({
    data: commonCases,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
  });

  const handleClickRow = useCallback(
    (_event: MouseEvent<HTMLTableRowElement>, row: Row<UserCommonCase>) => {
      entityDialogState.onOpen(row.original.Id);
    },
    []
  );

  const handleCloseDialog = () => {
    entityDialogState.onClose();
  };

  useEffect(
    () => () => {
      reset();
    },
    []
  );

  return (
    <>
      <Grid isLoading={isFetching} table={table} onClickRow={handleClickRow} />
      <Dialog
        open={entityDialogState.open}
        maxWidth="lg"
        fullWidth
        onClose={handleCloseDialog}
        TransitionProps={{
          onExited: entityDialogState.onClearId,
        }}
      >
        <UserCommonCaseViewForm caseId={entityDialogState.id!} />
      </Dialog>
    </>
  );
};

export default CommonCasesGrid;
